import { useCallback, useEffect, useMemo, useState } from 'react';
import reportsService from '../service/reportsService.mjs';
import { quarterOf } from '../util/aggregate.mjs';

const NO_FILTERS = { region: 'all', year: 'all' };

/** Seed's fiscal year of a report period: Sep-Aug, named for the year it ends. */
const fiscalYearOfPeriod = (month, year) => (Number(month) >= 9 ? Number(year) + 1 : Number(year));

/** A report period inside the Reports page's fiscal year, quarter and region. */
const inPageScope = (r, scope) =>
  fiscalYearOfPeriod(r.month, r.year) === Number(scope.fy)
  && (!scope.quarter || scope.quarter === 'all' || quarterOf(r.month) === Number(scope.quarter))
  && (!scope.region || scope.region === 'all' || r.region === scope.region);

/**
 * Finally approved reports, filtered in the browser.
 *
 * The server already narrows the list to what the caller may see, and it is
 * one row per region per month, so fetching it once and filtering here keeps
 * the region and year pickers built from what actually exists.
 *
 * With `scope` (the Reports page's filters - the Downloads tab passes it) the
 * list follows the page's fiscal year, quarter and region instead, so the one
 * toolbar at the top of the page filters everything on it. Without it the
 * list keeps its own region and year pickers.
 */
export const useReportArchive = (scope = null) => {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(NO_FILTERS);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAll(await reportsService.getArchive());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const regions = useMemo(() => [...new Set(all.map(r => r.region))].sort(), [all]);
  const years = useMemo(() => [...new Set(all.map(r => r.year))].sort((a, b) => b - a), [all]);

  const scopeFy = scope?.fy;
  const scopeQuarter = scope?.quarter;
  const scopeRegion = scope?.region;
  const scoped = !!scope;
  const reports = useMemo(() => all.filter(r => (scoped
    ? inPageScope(r, { fy: scopeFy, quarter: scopeQuarter, region: scopeRegion })
    : (filters.region === 'all' || r.region === filters.region)
      && (filters.year === 'all' || String(r.year) === String(filters.year)))
  ), [all, filters, scoped, scopeFy, scopeQuarter, scopeRegion]);

  const updateFilter = (key, value) => setFilters(f => ({ ...f, [key]: value }));
  const clearFilters = () => setFilters(NO_FILTERS);

  return {
    reports, totalCount: all.length, regions, years,
    filters, updateFilter, clearFilters, scoped,
    loading, error, reload: load,
  };
};

export default useReportArchive;
